import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fab,
  Slide,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { GetApp as InstallIcon, Close as CloseIcon } from '@mui/icons-material';
import { showInstallPrompt, isPWAInstalled } from '../utils/serviceWorkerRegistration';

const Transition = React.forwardRef((
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<HTMLDivElement>,
) => <Slide direction="up" ref={ref} {...props} />);

interface PWAInstallPromptProps {
  variant?: 'fab' | 'button' | 'auto';
  showPromptAfterDelay?: number; // milliseconds
}

const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({
  variant = 'auto',
  showPromptAfterDelay = 5000 // 5 seconds
}) => {
  const [isInstallable, setIsInstallable] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if PWA is already installed
    setIsInstalled(isPWAInstalled());

    // Listen for install availability
    const handleInstallAvailable = () => {
      setIsInstallable(true);

      // Auto-show prompt after delay if variant is auto
      if (variant === 'auto' && !isPWAInstalled()) {
        setTimeout(() => {
          setShowDialog(true);
        }, showPromptAfterDelay);
      }
    };

    // Listen for successful installation
    const handleInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setShowDialog(false);
      setShowSnackbar(true);
    };

    window.addEventListener('pwaInstallAvailable', handleInstallAvailable);
    window.addEventListener('pwaInstalled', handleInstalled);

    return () => {
      window.removeEventListener('pwaInstallAvailable', handleInstallAvailable);
      window.removeEventListener('pwaInstalled', handleInstalled);
    };
  }, [variant, showPromptAfterDelay]);

  const handleInstallClick = () => {
    if (variant === 'fab' || variant === 'button') {
      setShowDialog(true);
    } else {
      showInstallPrompt();
    }
  };

  const handleInstallConfirm = () => {
    showInstallPrompt();
    setShowDialog(false);
  };

  const handleClose = () => {
    setShowDialog(false);
  };

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  // Don't render if PWA is already installed or not installable
  if (isInstalled || !isInstallable) {
    return null;
  }

  return (
    <>
      {/* Floating Action Button */}
      {variant === 'fab' && (
        <Fab
          color="primary"
          aria-label="Install TruckSight"
          onClick={handleInstallClick}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000
          }}
        >
          <InstallIcon />
        </Fab>
      )}

      {/* Button */}
      {variant === 'button' && (
        <Button
          variant="contained"
          startIcon={<InstallIcon />}
          onClick={handleInstallClick}
          sx={{ margin: 1 }}
        >
          Install App
        </Button>
      )}

      {/* Install Dialog */}
      <Dialog
        open={showDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="install-dialog-description"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Install TruckSight
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="install-dialog-description">
            Install TruckSight on your device for faster access and offline functionality.
            The app will be added to your home screen and can work even when you&apos;re offline.
          </DialogContentText>
          <ul style={{ marginTop: 16, paddingLeft: 20 }}>
            <li>Faster loading times</li>
            <li>Offline access to cached data</li>
            <li>Push notifications for important updates</li>
            <li>Native app-like experience</li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Not Now
          </Button>
          <Button onClick={handleInstallConfirm} color="primary" variant="contained">
            Install
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          TruckSight has been installed successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default PWAInstallPrompt;
